import React, { useEffect, useState } from 'react';
import {
  AreaChart,
  Area,
  CartesianGrid,
  XAxis,
  YAxis,
  ResponsiveContainer,
  Tooltip,
} from 'recharts';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCaretUp, faCaretDown } from '@fortawesome/free-solid-svg-icons';
import { fetchHistory } from '../utils/marketdata/currency';
import styles from '../styles/marketdata/RateHistoryChart.module.scss';

const SERIES_COLOR = '#2563eb'; // $pwa-info — o singură serie, deci titlul o denumește; fără legendă

const fmt = (n, digits = 4) =>
  new Intl.NumberFormat('ro-RO', {
    minimumFractionDigits: digits,
    maximumFractionDigits: digits,
  }).format(n);

// '07-09-2026' → '7 sep'
const MONTHS = ['ian', 'feb', 'mar', 'apr', 'mai', 'iun', 'iul', 'aug', 'sep', 'oct', 'noi', 'dec'];
const shortDate = (d) => {
  if (!d) return '';
  const [day, month] = d.split('-');
  return `${parseInt(day, 10)} ${MONTHS[parseInt(month, 10) - 1] || ''}`;
};

const ChartTooltip = ({ active, payload, currency }) => {
  if (!active || !payload || !payload.length) return null;
  const p = payload[0].payload;
  return (
    <div className={styles.tooltip}>
      <div className={styles.tooltipDate}>{shortDate(p.date)}</div>
      <div className={styles.tooltipValue}>
        1 {currency.toUpperCase()} = {fmt(p.value)} RON
      </div>
    </div>
  );
};

const RateHistoryChart = ({ currency }) => {
  const [state, setState] = useState({ points: null, fromCache: false, loading: true });

  useEffect(() => {
    let alive = true;
    setState((s) => ({ ...s, loading: true }));
    fetchHistory(currency, 30).then((res) => {
      if (!alive) return;
      setState({
        points: res ? res.points : null,
        fromCache: res ? res.fromCache : false,
        loading: false,
      });
    });
    return () => {
      alive = false;
    };
  }, [currency]);

  const { points, fromCache, loading } = state;

  if (loading) return <div className={styles.placeholder}>Se încarcă evoluția…</div>;
  if (!points || points.length < 2) return null;

  const first = points[0].value;
  const last = points[points.length - 1].value;
  const pct = ((last - first) / first) * 100;
  const up = pct >= 0;

  // Domeniu cu puțin aer, ca linia să nu atingă marginile cardului.
  const values = points.map((p) => p.value);
  const min = Math.min(...values);
  const max = Math.max(...values);
  const pad = (max - min) * 0.25 || max * 0.002;

  // Pe ~21 de puncte, etichetele pe X se calcă. Afișăm doar capetele și mijlocul,
  // ca în modelul de referință.
  const tickIdx = [0, Math.floor(points.length / 2), points.length - 1];
  const ticks = tickIdx.map((i) => points[i].date);

  return (
    <section className={styles.card} aria-label={`Evoluția cursului ${currency.toUpperCase()}/RON`}>
      <header className={styles.head}>
        <div>
          <h2 className={styles.title}>{currency.toUpperCase()} / RON</h2>
          <p className={styles.subtitle}>Ultimele 30 de zile · curs BNR</p>
        </div>
        <span className={up ? styles.up : styles.down}>
          <FontAwesomeIcon icon={up ? faCaretUp : faCaretDown} height="14" width="14" />
          {Math.abs(pct).toFixed(2)}%
        </span>
      </header>

      <div className={styles.plot}>
        <ResponsiveContainer width="100%" height="100%">
          <AreaChart data={points} margin={{ top: 6, right: 6, bottom: 0, left: -12 }}>
            <defs>
              <linearGradient id="rateFill" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor={SERIES_COLOR} stopOpacity={0.22} />
                <stop offset="100%" stopColor={SERIES_COLOR} stopOpacity={0.02} />
              </linearGradient>
            </defs>

            {/* grilă recesivă: doar orizontală, fără linii verticale care taie aria */}
            <CartesianGrid vertical={false} stroke="#e2e8ee" />

            <XAxis
              dataKey="date"
              ticks={ticks}
              tickFormatter={shortDate}
              tick={{ fontSize: 11, fill: '#6b7c8a' }}
              axisLine={false}
              tickLine={false}
              interval="preserveStartEnd"
            />
            <YAxis
              domain={[min - pad, max + pad]}
              tickFormatter={(v) => fmt(v, 2)}
              tick={{ fontSize: 11, fill: '#6b7c8a' }}
              axisLine={false}
              tickLine={false}
              width={46}
            />
            <Tooltip
              content={<ChartTooltip currency={currency} />}
              cursor={{ stroke: '#9fb3c2', strokeWidth: 1, strokeDasharray: '3 3' }}
            />
            <Area
              type="monotone"
              dataKey="value"
              stroke={SERIES_COLOR}
              strokeWidth={2}
              fill="url(#rateFill)"
              dot={false}
              activeDot={{ r: 4, strokeWidth: 2, stroke: '#fff' }}
            />
          </AreaChart>
        </ResponsiveContainer>
      </div>

      {/* Valorile se pot citi și fără grafic — pentru cine nu distinge culoarea
          sau nu poate folosi hover-ul. */}
      <footer className={styles.foot}>
        <span>
          Minim <strong>{fmt(min)}</strong>
        </span>
        <span>
          Maxim <strong>{fmt(max)}</strong>
        </span>
        <span>
          Acum <strong>{fmt(last)}</strong>
        </span>
      </footer>

      {fromCache && <p className={styles.offline}>Date salvate — nu am putut verifica acum.</p>}
    </section>
  );
};

export default RateHistoryChart;
